import type { Chart, SongMeta } from '@/types';
import type { AudioManager } from './AudioManager';
import { DEFAULT_NOTE_TRAVEL_TIME } from '@/game/config/GameConfig';
import { computeBeat, computeBeatProgress, computeNoteY, computeSongProgress } from '@/utils/rhythmMath';

export type RhythmEngineState = 'idle' | 'playing' | 'paused' | 'finished';

/**
 * The song clock. Everything about "where is this note right now" and "was this hit
 * on time" is derived from `currentSongTime`, which itself always comes from
 * AudioManager's live AudioContext clock -- never from a separately-ticked timer.
 * That's the single most important architectural decision in this project: it means
 * a dropped frame delays what the player *sees* for one frame, but never desyncs
 * scoring or note position, because both are recomputed fresh from audio time.
 */
export class RhythmEngine {
  private state: RhythmEngineState = 'idle';
  private audioOffsetSec = 0;
  readonly noteTravelTime: number;

  constructor(
    private audio: AudioManager,
    private song: SongMeta,
    chart: Chart,
    private buffer: AudioBuffer
  ) {
    this.noteTravelTime = chart.noteTravelTime ?? DEFAULT_NOTE_TRAVEL_TIME;
  }

  /** Calibration offset in milliseconds (positive delays the audio relative to the
   * hit windows/visuals, compensating for output latency). */
  setAudioOffsetMs(ms: number): void {
    this.audioOffsetSec = ms / 1000;
  }

  start(fromSeconds = 0): void {
    this.audio.play(this.buffer, fromSeconds);
    this.state = 'playing';
  }

  pause(): void {
    if (this.state !== 'playing') return;
    this.audio.pause();
    this.state = 'paused';
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.audio.resume();
    this.state = 'playing';
  }

  seek(seconds: number): void {
    this.audio.seek(seconds);
  }

  stop(): void {
    this.audio.stop();
    this.state = 'idle';
  }

  markFinished(): void {
    this.state = 'finished';
  }

  getState(): RhythmEngineState {
    return this.state;
  }

  get currentSongTime(): number {
    return this.audio.getCurrentTime() + this.audioOffsetSec;
  }

  get currentBeat(): number {
    return computeBeat(this.currentSongTime, this.song.bpm);
  }

  get beatProgress(): number {
    return computeBeatProgress(this.currentSongTime, this.song.bpm);
  }

  get songProgress(): number {
    return computeSongProgress(this.currentSongTime, this.song.duration);
  }

  get duration(): number {
    return this.song.duration;
  }

  get bpm(): number {
    return this.song.bpm;
  }

  isPlaying(): boolean {
    return this.state === 'playing';
  }

  isFinished(): boolean {
    return this.state === 'finished' || this.currentSongTime >= this.song.duration;
  }

  /** The Y position (internal px) a note targeting `targetTime` should currently be
   * drawn at, given where it spawns and where the catch line sits. */
  getNoteY(targetTime: number, spawnY: number, catchY: number): number {
    return computeNoteY(targetTime, this.currentSongTime, this.noteTravelTime, spawnY, catchY);
  }
}
